import { makeAssistantToolUI } from '@assistant-ui/react';
import { useMemo, useState } from 'react';
import {
    displayProfilesToolSignature,
    getInsideNetworkRecommendationsToolSignature,
    getProspectsInfoToolSignature,
    searchLinkedinNetworkRegexToolSignature,
    searchMyAssignedContactsToolSignature,
} from '@andsend/services-agent-shared';
import { z } from 'zod';
import { LinkedinProfileDto } from '@andsend/services-linkedin-profiles-shared';
import { DisplayContactsFromIdsUI } from './display-profiles/display-profiles-table';
import { GenericToolUI } from './generic-tool-ui';
import { Button } from '@shadcn/ui/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
    ContentPartStatus,
    ToolCallContentPartStatus,
} from 'node_modules/@assistant-ui/react/dist/types/AssistantTypes';

const CollapsibleContactsList = <T extends Record<string, any>, U extends Record<string, any>>({
    profiles,
    status,
    toolSignature,
    args,
    toolStatus,
}: {
    profiles: { linkedinProfileUrl: string | null; linkedinUserId: string | null; prospectId: string | null }[];
    status: 'running' | 'complete' | 'incomplete' | 'requires-action';
    toolSignature: any;
    args: T;
    toolStatus: ContentPartStatus | ToolCallContentPartStatus;
}) => {
    return (
        <div className="w-full flex flex-col">
            <GenericToolUI toolSignature={toolSignature} args={args} status={toolStatus}>
                <div className="p-4">
                    <DisplayContactsFromIdsUI profiles={profiles} status={status} className="py-0" />
                </div>
            </GenericToolUI>
        </div>
    );
};

export const DisplayProfilesToolUI = makeAssistantToolUI<
    z.infer<typeof displayProfilesToolSignature.inputSchema>,
    z.infer<typeof displayProfilesToolSignature.outputSchema>
>({
    toolName: displayProfilesToolSignature.name,
    render: ({ args, result, status }) => {
        const profiles = useMemo(
            () => [
                ...(result?.memberIds?.map((id) => ({
                    linkedinUserId: id,
                    linkedinProfileUrl: null,
                    prospectId: null,
                })) || []),
                ...(result?.linkedinProfileUrls?.map((url) => ({
                    linkedinProfileUrl: url,
                    linkedinUserId: null,
                    prospectId: null,
                })) || []),
                ...(result?.prospectIds?.map((id) => ({
                    linkedinProfileUrl: null,
                    linkedinUserId: null,
                    prospectId: id,
                })) || []),
            ],
            [result?.memberIds, result?.linkedinProfileUrls, result?.prospectIds]
        );

        return DisplayContactsFromIdsUI({
            profiles,
            status: status.type,
        });
    },
});

export const SearchLinkedinNetworkToolUI = makeAssistantToolUI<
    z.infer<typeof searchLinkedinNetworkRegexToolSignature.inputSchema>,
    z.infer<typeof searchLinkedinNetworkRegexToolSignature.outputSchema>
>({
    toolName: searchLinkedinNetworkRegexToolSignature.name,
    render: ({ args, result, status }) => {
        return (
            <CollapsibleContactsList
                toolSignature={searchLinkedinNetworkRegexToolSignature}
                args={args}
                toolStatus={status}
                profiles={
                    result?.profiles?.map((profile) => ({
                        linkedinProfileUrl: LinkedinProfileDto.linkedinProfileUrlFromPublicIdentifier(
                            profile.publicIdentifier
                        ),
                        linkedinUserId: profile.memberId,
                        prospectId: null,
                    })) || []
                }
                status={status.type}
            />
        );
    },
});

export const GetProspectsInfoToolUI = makeAssistantToolUI<
    z.infer<typeof getProspectsInfoToolSignature.inputSchema>,
    z.infer<typeof getProspectsInfoToolSignature.outputSchema>
>({
    toolName: getProspectsInfoToolSignature.name,
    render: ({ args, result, status }) => {
        return (
            <CollapsibleContactsList
                toolSignature={getProspectsInfoToolSignature}
                args={args}
                toolStatus={status}
                profiles={
                    result?.prospects?.map((prospect) => ({
                        linkedinProfileUrl: null,
                        linkedinUserId: null,
                        prospectId: prospect.id,
                    })) || []
                }
                status={status.type}
            />
        );
    },
});

export const SearchMyAssignedContactsToolUI = makeAssistantToolUI<
    z.infer<typeof searchMyAssignedContactsToolSignature.inputSchema>,
    z.infer<typeof searchMyAssignedContactsToolSignature.outputSchema>
>({
    toolName: searchMyAssignedContactsToolSignature.name,
    render: ({ args, result, status }) => {
        return (
            <CollapsibleContactsList
                toolSignature={searchMyAssignedContactsToolSignature}
                args={args}
                toolStatus={status}
                profiles={
                    result?.contacts?.map((contact) => ({
                        linkedinProfileUrl: null,
                        linkedinUserId: null,
                        prospectId: contact.prospectId,
                    })) || []
                }
                status={status.type}
            />
        );
    },
});

export const GetInsideNetworkRecommendationsToolUI = makeAssistantToolUI<
    z.infer<typeof getInsideNetworkRecommendationsToolSignature.inputSchema>,
    z.infer<typeof getInsideNetworkRecommendationsToolSignature.outputSchema>
>({
    toolName: getInsideNetworkRecommendationsToolSignature.name,
    render: ({ args, result, status }) => {
        return (
            <CollapsibleContactsList
                toolSignature={getInsideNetworkRecommendationsToolSignature}
                args={args}
                toolStatus={status}
                profiles={
                    result?.map((recommendation) => ({
                        linkedinProfileUrl: recommendation.linkedinProfileUrl,
                        linkedinUserId: null,
                        prospectId: null,
                    })) || []
                }
                status={status.type}
            />
        );
    },
});
